'use client';

import { useTranslations } from 'next-intl';
import { ChevronDown, FlaskConical } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { isDevToolsAllowedForEmail } from '@/features/dev-tools';
import {
  useDevQrInjectStore,
  type DevQrInjectMode
} from '../store/dev-qr-inject-store';

const MODES: DevQrInjectMode[] = ['off', 'match', 'mismatch', 'garbage'];

/**
 * Dev-only switch that controls whether uploaded/scanned images get a synthetic
 * QR overlay applied client-side before they leave the browser. Lets QA hit
 * every backend branch (success / mismatched / unverified) without printing
 * real sheets. Gated by the dev-tools email allowlist.
 */
export function DevQrInjectorPanel({ className }: { className?: string }) {
  const t = useTranslations('photoScanning');
  const { user } = useAuth();
  const mode = useDevQrInjectStore((s) => s.mode);
  const setMode = useDevQrInjectStore((s) => s.setMode);
  const sequenceOverride = useDevQrInjectStore((s) => s.sequenceOverride);
  const setSequenceOverride = useDevQrInjectStore((s) => s.setSequenceOverride);

  if (!isDevToolsAllowedForEmail(user?.email)) return null;

  // The "Target P{n}" input is only meaningful when the injector actually
  // bakes a QR into the image, so we hide it for `off` / `garbage`.
  const showSequenceOverride = mode === 'match' || mode === 'mismatch';

  // A non-`off` mode is worth surfacing at a glance even while collapsed, so the
  // dev doesn't forget injection is armed. `off` stays quiet.
  const armed = mode !== 'off';

  return (
    <details
      className={cn(
        'group rounded-md border border-dashed border-amber-300 bg-amber-50/70 dark:border-amber-700 dark:bg-amber-950/30',
        className
      )}
    >
      <summary className='flex cursor-pointer list-none items-center gap-2 px-2 py-1.5'>
        <FlaskConical
          className='h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400'
          aria-hidden
        />
        <span className='text-[10px] font-bold tracking-wider text-amber-700 uppercase dark:text-amber-300'>
          {t('devInjectQr')}
        </span>
        {armed && (
          <span className='rounded-full bg-amber-200 px-1.5 font-mono text-[9px] font-bold text-amber-900 dark:bg-amber-900 dark:text-amber-100'>
            {t(`devInjectQrMode_${mode}`)}
          </span>
        )}
        <ChevronDown
          className='ms-auto h-3.5 w-3.5 text-amber-600 transition-transform group-open:rotate-180 dark:text-amber-400'
          aria-hidden
        />
      </summary>
      <div className='flex flex-wrap items-center gap-2 px-2 pt-1 pb-2'>
        <Select
          value={mode}
          onValueChange={(value) => setMode(value as DevQrInjectMode)}
        >
          <SelectTrigger
            size='sm'
            className='h-7 min-w-[100px] border-amber-300 bg-white text-xs dark:border-amber-700 dark:bg-slate-900'
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODES.map((m) => (
              <SelectItem key={m} value={m} className='text-xs'>
                {t(`devInjectQrMode_${m}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showSequenceOverride && (
          <label
            className='flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300'
            title={t('devInjectTargetHint')}
          >
            <span>{t('devInjectTarget')}</span>
            <span className='font-mono'>P</span>
            <input
              type='number'
              min={1}
              step={1}
              inputMode='numeric'
              placeholder='—'
              value={sequenceOverride ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (v === '') {
                  setSequenceOverride(null);
                  return;
                }
                const n = Number.parseInt(v, 10);
                setSequenceOverride(Number.isFinite(n) && n > 0 ? n : null);
              }}
              className='h-7 w-14 rounded-md border border-amber-300 bg-white px-1.5 text-center font-mono text-xs text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 dark:border-amber-700 dark:bg-slate-900 dark:text-slate-100'
            />
          </label>
        )}
      </div>
    </details>
  );
}
